import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableActionFilter, TableMenuItem } from '@urbantxt/table';
import { PickerItem } from 'types';
import {
  openErrorToaster,
  openSuccessToaster,
} from 'services/toast.service';
import { getBagelDocumentViewPath } from 'utils/navigation';
import {
  deleteBagelDocument,
  getBagelDocumentIngestYears,
  getBagelDocumentStatusSummary,
  getBagelDocumentsPagination,
  reprocessBagelDocument,
  reprocessBagelDocumentsYear,
  type BagelDocumentStatusSummary,
} from '../../api/bagel-documents';
import { BagelDocument, BagelDocumentStatus } from '../../types';
import useBagelDocumentsTableSearchParams from './useBagelDocumentsTableSearchParams';
import { CATEGORY_OPTIONS } from '../constants/bagelDocumentFormOptions';
import {
  BagelDocumentFilterState,
  BagelDocumentTableRow,
  FILE_TYPE_FILTER_OPTIONS,
  normalizeBagelDocumentRow,
} from '../components/DocumentsTab/documentTableFilters';

export interface UseBagelDocumentsTabParams {
  readOnly?: boolean;
}

export default function useBagelDocumentsTab({
  readOnly = false,
}: UseBagelDocumentsTabParams = {}) {
  const navigate = useNavigate();
  const {
    page,
    limit,
    sortColumn,
    sortType,
    search,
    filters,
    onPageChange,
    onChangeLimit,
    onSortColumn,
    debouncedSearch,
    setFilter,
    clearAllFilters,
  } = useBagelDocumentsTableSearchParams();
  const [tableRows, setTableRows] = useState<BagelDocumentTableRow[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<BagelDocument | null>(null);
  const [bulkReprocessing, setBulkReprocessing] = useState(false);
  const [statusSummary, setStatusSummary] =
    useState<BagelDocumentStatusSummary | null>(null);
  const [ingestYears, setIngestYears] = useState<string[]>([]);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef<number | null>(null);

  const refresh = useCallback(() => setRefreshIndex(i => i + 1), []);

  const loadPage = useCallback(async () => {
    const result = await getBagelDocumentsPagination(
      { page, limit, sortColumn, sortType, search },
      {
        ingestSource: filters.ingestSource,
        domain: filters.domain,
        category: filters.category,
        status: filters.status,
        year: filters.year,
        fileType: filters.fileType,
      },
    );

    if (result.totalPages > 0 && page > result.totalPages) {
      onPageChange(result.totalPages);
      return;
    }

    setTableRows(result.docs.map(normalizeBagelDocumentRow));
    setTotalDocs(result.totalDocs);
  }, [page, limit, sortColumn, sortType, search, filters, onPageChange]);

  const loadSummary = useCallback(async () => {
    const summary = await getBagelDocumentStatusSummary({
      domain: filters.domain !== 'all' ? filters.domain : undefined,
      year: filters.year !== 'all' ? filters.year : undefined,
    });
    setStatusSummary(summary);
    return summary;
  }, [filters.domain, filters.year]);

  const loadIngestYears = useCallback(async () => {
    const years = await getBagelDocumentIngestYears(
      filters.domain !== 'all' ? filters.domain : undefined,
    );
    setIngestYears(years);
  }, [filters.domain]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([loadPage(), loadSummary(), loadIngestYears()]);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load documents',
      );
    } finally {
      setLoading(false);
    }
  }, [loadPage, loadSummary, loadIngestYears]);

  useEffect(() => {
    let mounted = true;

    async function fetchPage() {
      try {
        setLoading(true);
        await loadPage();
        if (mounted) setError(null);
      } catch (err) {
        if (mounted) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to load documents',
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchPage();

    return () => {
      mounted = false;
    };
  }, [loadPage, refreshIndex]);

  useEffect(() => {
    let mounted = true;

    async function fetchMeta() {
      try {
        const summary = await loadSummary();
        if (!mounted) return;
        inFlightRef.current = summary.pending + summary.processing;
      } catch {
        // summary errors are non-fatal; page load handles primary error state
      }
    }

    fetchMeta();
    loadIngestYears().catch(() => {});

    return () => {
      mounted = false;
    };
  }, [loadSummary, loadIngestYears, refreshIndex]);

  useEffect(() => {
    const inFlight =
      (statusSummary?.pending ?? 0) + (statusSummary?.processing ?? 0);

    const pollSummary = async () => {
      try {
        const summary = await loadSummary();
        const newInFlight = summary.pending + summary.processing;
        const prevInFlight = inFlightRef.current;
        inFlightRef.current = newInFlight;

        if (prevInFlight !== null && prevInFlight !== newInFlight) {
          refresh();
        }
      } catch {
        // ignore polling errors
      }
    };

    if (inFlight > 0 && !pollingRef.current) {
      pollingRef.current = setInterval(pollSummary, 3000);
    }

    if (inFlight === 0 && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [
    statusSummary?.pending,
    statusSummary?.processing,
    loadSummary,
    refresh,
  ]);

  const ingestYearOptions: PickerItem[] = useMemo(
    () => [
      { label: 'All years', value: 'all', role: '' },
      ...ingestYears.map(year => ({ label: year, value: year, role: '' })),
    ],
    [ingestYears],
  );

  const tableFilters: TableActionFilter[] = useMemo(
    () => [
      {
        value: filters.ingestSource,
        pickerOptions: [
          { label: 'All sources', value: 'all', role: '' },
          { label: 'Rosie upload', value: 'rosie', role: '' },
          { label: 'MCP ingest', value: 'mcp', role: '' },
        ],
        pickerLabel: 'Ingest source',
        valueLabel: '',
        clearFilters: () => setFilter('ingestSource', 'all'),
        onFilterChange: v =>
          setFilter(
            'ingestSource',
            (v || 'all') as BagelDocumentFilterState['ingestSource'],
          ),
      },
      {
        value: filters.domain,
        pickerOptions: [
          { label: 'All domains', value: 'all', role: '' },
          { label: 'Org', value: 'org', role: '' },
          { label: 'SCLA', value: 'scla', role: '' },
        ],
        pickerLabel: 'Domain',
        valueLabel: '',
        clearFilters: () => setFilter('domain', 'all'),
        onFilterChange: v =>
          setFilter(
            'domain',
            (v || 'all') as BagelDocumentFilterState['domain'],
          ),
      },
      {
        value: filters.category,
        pickerOptions: [
          { label: 'All categories', value: 'all', role: '' },
          { label: 'None (unset)', value: 'null', role: '' },
          ...CATEGORY_OPTIONS.map(opt => ({
            label: opt.label,
            value: opt.value,
            role: '',
          })),
        ],
        pickerLabel: 'Category',
        valueLabel: '',
        clearFilters: () => setFilter('category', 'all'),
        onFilterChange: v =>
          setFilter(
            'category',
            (v || 'all') as BagelDocumentFilterState['category'],
          ),
      },
      {
        value: filters.status,
        pickerOptions: [
          { label: 'All statuses', value: 'all', role: '' },
          { label: 'Ready', value: 'ready', role: '' },
          { label: 'Processing', value: 'processing', role: '' },
          { label: 'Pending', value: 'pending', role: '' },
          { label: 'Error', value: 'error', role: '' },
        ],
        pickerLabel: 'Status',
        valueLabel: '',
        clearFilters: () => setFilter('status', 'all'),
        onFilterChange: v =>
          setFilter(
            'status',
            (v || 'all') as BagelDocumentFilterState['status'],
          ),
      },
      {
        value: filters.fileType,
        pickerOptions: [
          { label: 'All types', value: 'all', role: '' },
          ...FILE_TYPE_FILTER_OPTIONS.map(opt => ({
            label: opt.label,
            value: opt.value,
            role: '',
          })),
        ],
        pickerLabel: 'File type',
        valueLabel: '',
        clearFilters: () => setFilter('fileType', 'all'),
        onFilterChange: v =>
          setFilter(
            'fileType',
            (v || 'all') as BagelDocumentFilterState['fileType'],
          ),
      },
      ...(ingestYearOptions.length > 1
        ? [
            {
              value: filters.year,
              pickerOptions: ingestYearOptions,
              pickerLabel: 'Ingest year',
              valueLabel: '',
              clearFilters: () => setFilter('year', 'all'),
              onFilterChange: (v: string | null) =>
                setFilter('year', v || 'all'),
            } satisfies TableActionFilter,
          ]
        : []),
    ],
    [filters, ingestYearOptions, setFilter],
  );

  const handleViewDocument = useCallback(
    (docId: string) => {
      navigate(getBagelDocumentViewPath(docId));
    },
    [navigate],
  );

  const handleOpenPreview = useCallback(
    (docId: string) => {
      navigate(getBagelDocumentViewPath(docId, 'preview'));
    },
    [navigate],
  );

  const handleReprocess = useCallback(
    async (id: string) => {
      await reprocessBagelDocument(id);
      setTableRows(prev =>
        prev.map(d =>
          d._id === id
            ? { ...d, status: 'pending' as BagelDocumentStatus }
            : d,
        ),
      );
      refresh();
    },
    [refresh],
  );

  const handleBulkReprocessYear = useCallback(async () => {
    if (filters.year === 'all') return;
    if (
      // eslint-disable-next-line no-alert -- destructive bulk action; confirm before re-embedding all year chunks
      !window.confirm(
        `Re-process all SCLA documents for ${filters.year}? This re-embeds every chunk for the year.`,
      )
    ) {
      return;
    }

    setBulkReprocessing(true);
    try {
      const result = await reprocessBagelDocumentsYear(filters.year);
      openSuccessToaster(
        `Re-processing started for ${result.started} document(s) in ${result.year}`,
        5000,
      );
      refresh();
    } catch (err) {
      openErrorToaster(
        err instanceof Error ? err.message : 'Bulk re-process failed',
        5000,
      );
    } finally {
      setBulkReprocessing(false);
    }
  }, [filters.year, refresh]);

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete "${name}" and all its embeddings?`))
        return;
      await deleteBagelDocument(id);
      refresh();
    },
    [refresh],
  );

  const rowMenuItemsGenerator = useCallback(
    (row: BagelDocumentTableRow): TableMenuItem[] => {
      const items: TableMenuItem[] = [
        {
          label: 'View details',
          onSelect: handleViewDocument,
        },
        {
          label: 'Open preview',
          onSelect: handleOpenPreview,
        },
      ];

      if (!readOnly) {
        items.push(
          {
            label: 'Edit metadata',
            onSelect: id => {
              const doc = tableRows.find(d => d._id === id);
              if (doc) setEditingDoc(doc);
            },
          },
          {
            label: 'Reprocess',
            disabled: row.status === 'processing',
            onSelect: handleReprocess,
          },
          {
            label: 'Delete',
            onSelect: id => {
              const doc = tableRows.find(d => d._id === id);
              if (doc) handleDelete(id, doc.name);
            },
          },
        );
      }

      return items;
    },
    [
      readOnly,
      handleViewDocument,
      handleOpenPreview,
      handleReprocess,
      handleDelete,
      tableRows,
    ],
  );

  const menuColumn = useMemo(
    () => ({
      menuItemsGenerator: rowMenuItemsGenerator,
    }),
    [rowMenuItemsGenerator],
  );

  const handleUploaded = useCallback(
    (doc: BagelDocument) => {
      setUploadOpen(false);
      if (page === 1) {
        setTableRows(prev => [
          normalizeBagelDocumentRow(doc),
          ...prev.slice(0, limit - 1),
        ]);
        setTotalDocs(prev => prev + 1);
      } else {
        refresh();
      }
    },
    [page, limit, refresh],
  );

  const handleUpdated = useCallback((updated: BagelDocument) => {
    setTableRows(prev =>
      prev.map(d =>
        d._id === updated._id ? normalizeBagelDocumentRow(updated) : d,
      ),
    );
    setEditingDoc(null);
  }, []);

  return {
    page,
    limit,
    sortColumn,
    sortType,
    search,
    filters,
    onPageChange,
    onChangeLimit,
    onSortColumn,
    debouncedSearch,
    clearAllFilters,
    setFilter,
    tableRows,
    totalDocs,
    loading,
    error,
    statusSummary,
    load,
    uploadOpen,
    setUploadOpen,
    editingDoc,
    setEditingDoc,
    bulkReprocessing,
    tableFilters,
    handleReprocess,
    handleBulkReprocessYear,
    menuColumn,
    handleUploaded,
    handleUpdated,
  };
}
